import { asArray, asFunction, isFunction, isNull, isObject, isTrue, isUndefined, isValue, toMap } from './utils';
import { BehaviorSubject, combineLatest, noop, Observable, of } from 'rxjs';
import { map, filter, switchMap, tap, debounceTime } from 'rxjs/operators';
import { GroupsManager } from './groups';

const DEFAULT_INDEX_PROP: string = 'id';
const ASTERISK = '*';

export enum CollectionProps {
  ITEM_ID = '__itemId',
  PARENT_COLLECTION = '__parentCollection',
  PARENT_ITEM = '__parentItem',
  CHILD_COLLECTION = '__childCollection',
  IS_SELECTED = '__isSelected',
  SOURCE = '__source',
  ORIGINAL_VALUE = '__original',
  OBSERVABLE_STATE = '__isSelectedObservable',
  ON_SELECT = '__onSelect',
}

export enum CollectionMethods {
  TOGGLE = '__toggle',
  SELECT = '__select',
  DESELECT = '__deselect',
  UPDATE = '__update',
  HAS_CLASS = '__hasClass',
  TOGGLE_CLASS = '__toggleClass',
  CREATE_CHILD_COLLECTION = '__createChildCollection',
}

export interface ICollectionConfig {
  name?: string | null;
  parentItem?: any | null;
  indexBy?: any;
  allowMultiselect?: boolean;
}

export class Collection<T> {
  private static collectionsCounter: number = 0;

  private itemsCounter: number = 0;

  private readonly config: ICollectionConfig = {
    name: null,
    parentItem: null,
    indexBy: DEFAULT_INDEX_PROP,
    allowMultiselect: false,
  };

  private readonly uid: string;

  private readonly collectionCore: Map<any, any> = new Map<any, any>();

  private readonly selectionCore: Set<any> = new Set<any>();

  private readonly restrictions: Set<any> = new Set<any>();

  private readonly placeholders: Set<any> = new Set<any>();

  private readonly collectionHash: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  private readonly selectionHash: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  private readonly classList$: GroupsManager = new GroupsManager<any, string>();

  public readonly isUpdating$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private defineCollectionItemId = (d: T | any): string => {
    const { indexBy } = this.config;
    return isUndefined(d) || isNull(d) ? null : `${d[indexBy] || d[CollectionProps.ITEM_ID] || d}`;
  };

  private getCollectionItem = (d: T | any): T | null => this.collectionCore.get(this.defineCollectionItemId(d)) || null;

  private getCollectionItemId = (d: T | any): any | null => {
    const item = this.getCollectionItem(d);
    return isNull(item) ? null : item[CollectionProps.ITEM_ID];
  };

  private getIdsList = (data: any, existingItemsOnly: boolean = false): any[] => {
    const { getCollectionItemId } = this;

    return asArray(data)
      .map(d => (existingItemsOnly ? getCollectionItemId(d) : isObject(d) ? null : `${d}`))
      .filter(isTrue);
  };

  private updateCollectionHash = (): this => {
    this.collectionHash.next(this.collectionHash.value + 1);
    return this;
  };

  private updateSelectionHash = (): this => {
    this.selectionHash.next(this.selectionHash.value + 1);
    return this;
  };

  private addToCollection = (item: T): boolean => {
    const itemIsObject = isObject(item);
    const collection = this;
    const { indexBy, parentItem } = this.config;
    const defaultItemId = `${this.uid}_item#${++this.itemsCounter}`;
    const id = `${itemIsObject && !isUndefined(item[indexBy]) ? item[indexBy] : defaultItemId}`;
    const core = itemIsObject ? item : { value: item };

    let childCollection = null;

    this.collectionCore.set(
      id,
      new Proxy<any>(core, {
        get(_: any, prop: PropertyKey | any): any {
          if (!isUndefined(item[prop])) {
            return item[prop];
          } else {
            switch (prop) {
              case CollectionProps.ORIGINAL_VALUE:
                return item;
              case CollectionProps.PARENT_COLLECTION:
                return collection;
              case CollectionProps.ITEM_ID:
                return id;
              case CollectionProps.PARENT_ITEM:
                return parentItem;
              case CollectionProps.IS_SELECTED:
                return collection.isSelected(id);
              case CollectionProps.OBSERVABLE_STATE:
                return collection.onSelect().pipe(map(() => collection.isSelected(id)));
              case CollectionProps.CHILD_COLLECTION:
                return childCollection;
              case CollectionProps.ON_SELECT:
                return collection.onSelect(id);
              case CollectionMethods.UPDATE:
                return (updateQuery: any) => {
                  collection.update({ ...updateQuery, [indexBy]: id });
                  return collection.getItem(id);
                };
              case CollectionMethods.TOGGLE:
                return (state: boolean | null = null, forceUpdate: boolean = false): T => {
                  collection.toggle(id, state, forceUpdate);
                  return collection.getItem(id);
                };
              case CollectionMethods.HAS_CLASS:
                return (className: string | any): boolean => collection.hasClass(className, id);
              case CollectionMethods.TOGGLE_CLASS:
                return (className: string | any, state: boolean | null = null) => {
                  collection.toggleClass(className, id, state);
                  return collection.getItem(id);
                };
              case CollectionMethods.CREATE_CHILD_COLLECTION:
                return <TT>(config: ICollectionConfig = {}): Collection<TT> => {
                  childCollection = new Collection<TT>(
                    Object.assign({}, config, { parentItem: collection.getItem(id) })
                  );
                  return childCollection;
                };
              default:
                return isObject ? core[prop] : item;
            }
          }
        },
      })
    );

    this.placeholders.delete(id);

    return true;
  };

  private removeFromCollection = (d: any): boolean => {
    const id = this.getCollectionItemId(d);
    if (isNull(id)) {
      return false;
    } else {
      this.collectionCore.delete(id);
      return true;
    }
  };

  private addToSelection = (d: any): boolean => {
    const id = this.getCollectionItemId(d);
    if (isNull(id)) {
      return false;
    } else {
      const { allowMultiselect } = this.config;
      if (!this.hasRestrictions() || this.restrictions.has(id)) {
        if (this.selectionCore.has(id)) {
          return false;
        } else {
          if (!allowMultiselect) {
            this.selectionCore.clear();
          }
          this.selectionCore.add(id);
          return true;
        }
      } else {
        return false;
      }
    }
  };

  private removeFromSelection = (d: any): boolean => {
    const id = this.getCollectionItemId(d);
    if (isNull(id)) {
      return false;
    } else {
      if (!this.selectionCore.has(id)) {
        return false;
      } else {
        this.selectionCore.delete(id);
        return true;
      }
    }
  };

  public hasItem = (d: any): boolean => {
    return isFunction(d) ? !!this.filter(d).length : !isNull(this.getCollectionItemId(d));
  };

  public isSelected = (d: any): boolean => this.selectionCore.has(this.getCollectionItemId(d));

  public isWaiting = (d: any): boolean => this.placeholders.has(`${d}`);

  public setWaiting = (d: any, state: boolean = true): this => {
    state ? this.placeholders.add(`${d}`) : this.placeholders.delete(`${d}`);
    return this;
  };

  public getItem = (id: any, pure: boolean = false): T | null => {
    const item = this.getCollectionItem(id);
    return isNull(item) ? null : pure ? item[CollectionProps.ORIGINAL_VALUE] : item;
  };

  public getItems = (ids: any | null = ASTERISK, pure: boolean = false): T[] => {
    const items =
      isNull(ids) || ids === ASTERISK
        ? this.items
        : asArray(ids)
            .filter(id => this.hasItem(id))
            .map(id => this.getItem(id));

    return pure ? items.map(item => item[CollectionProps.ORIGINAL_VALUE]) : items;
  };

  public getItemAsync = (d: any): Observable<T> => {
    const { defineCollectionItemId, hasItem, setWaiting, getItem, onChange } = this;
    const itemId = defineCollectionItemId(d);

    !hasItem(itemId) && setWaiting(itemId, true);

    return onChange().pipe(
      map(() => getItem(d)),
      filter(isTrue),
      tap(() => setWaiting(d, false))
    );
  };

  public getItemsAsync = (d: any): Observable<T[]> => {
    const { defineCollectionItemId, setWaiting, getItems, hasItem, onChange } = this;

    const ids = asArray(d).map(defineCollectionItemId);

    ids.forEach(id => !hasItem(id) && setWaiting(id, true));

    return onChange().pipe(
      map(() => getItems(ids)),
      tap(items => items.forEach(item => setWaiting(item, false))),
      filter(items => items.length === ids.length)
    );
  };

  public onItemChange = (id: any): Observable<T> => {
    return this.onChange().pipe(
      map(() => this.getItem(id)),
      filter(item => !!item)
    );
  };

  public onItemSelect = (id: any): Observable<T> => {
    return this.onSelect().pipe(
      filter(() => this.isSelected(id)),
      map(() => this.getItem(id))
    );
  };

  public getAsObservable = (ids: any | null = ASTERISK, pure: boolean = false): Observable<T[]> => {
    return this.onUpdated().pipe(map(() => this.getItems(ids, pure)));
  };

  public add = (data: T | T[], forceUpdate: boolean = false): this => {
    let wasUpdated = forceUpdate;
    asArray(data).forEach(d => (wasUpdated = this.addToCollection(d) || wasUpdated));
    return wasUpdated ? this.updateCollectionHash() : this;
  };

  public remove = (data: any, forceUpdate: boolean = false): this => {
    const { classList$, removeFromSelection, removeFromCollection, getCollectionItemId } = this;

    let wasUpdated = forceUpdate;

    asArray(data).forEach(d => {
      wasUpdated = removeFromSelection(d) || wasUpdated;
      wasUpdated = removeFromCollection(d) || wasUpdated;
      classList$.remove(getCollectionItemId(d));
    });

    return wasUpdated ? this.updateCollectionHash() : this;
  };

  public select = (data: any, forceUpdate: boolean = false): this => {
    let wasUpdated = false;
    asArray(data).forEach(d => (wasUpdated = this.addToSelection(d) || wasUpdated));
    return forceUpdate || wasUpdated ? this.updateSelectionHash() : this;
  };

  public selectFirst = (): this => this.select(this.indexes[0], true);

  public selectLast = (): this => this.select(this.indexes[this.size - 1], true);

  public deselect = (data: any, forceUpdate: boolean = false): this => {
    let wasUpdated = false;
    asArray(data).forEach(d => (wasUpdated = this.removeFromSelection(d) || wasUpdated));
    return forceUpdate || wasUpdated ? this.updateSelectionHash() : this;
  };

  public toggle = (data: any, state: boolean | null = null, forceUpdate: boolean = false): this => {
    let wasUpdated = false;

    if (isNull(state)) {
      asArray(data).forEach(d => {
        const newState = !this.isSelected(d);
        wasUpdated = (newState ? this.addToSelection(d) : this.removeFromSelection(d)) || wasUpdated;
      });
    } else {
      asArray(data).forEach(
        d => (wasUpdated = (!!state ? this.addToSelection(d) : this.removeFromSelection(d)) || wasUpdated)
      );
    }

    return forceUpdate || wasUpdated ? this.updateSelectionHash() : this;
  };

  public selectAll = (): this => (this.config.allowMultiselect ? this.select(this.indexes) : this);

  public deselectAll = (): this => this.deselect(this.indexes);

  public changeSelection = (data: any): this => {
    const itemsToSelect = toMap(this.getItems(data), this.config.indexBy);

    asArray(this.indexes).forEach(index => !itemsToSelect.has(index) && this.removeFromSelection(index));

    return this.select(data, true);
  };

  public refreshCollection = (): this => this.updateCollectionHash();

  public refreshSelection = (): this => this.updateSelectionHash();

  public onChange = (): Observable<T[]> => {
    return this.collectionHash.asObservable().pipe(map(() => this.items));
  };

  public removeAll = (): this => this.remove(this.indexes);

  public replaceAll = (data: any): this => this.removeAll().add(data);

  public update = (d: any): this => {
    asArray(d).forEach(dd => {
      const collectionItem = this.getCollectionItem(dd);
      if (collectionItem) {
        Object.keys(dd).forEach(key => {
          if (key !== this.config.indexBy) {
            try {
              collectionItem[key] = dd[key];
            } catch (err) {
              console.log(err);
            }
          }
        });
      }
    });
    return this.refreshCollection();
  };

  public updateItems = (d: any | null = ASTERISK, updateQueries: any = {}): this => {
    // More useful update query
    const items = this.getItems(d);
    const { indexBy } = this.config;
    let wasUpdated = false;

    updateQueries = asArray(updateQueries);

    items.forEach((dd, i) => {
      const updateQuery = updateQueries[i] || updateQueries[0];
      Object.keys(updateQuery).forEach(key => {
        if (key !== indexBy) {
          const valA = dd[key];
          const valB = updateQuery[key];

          try {
            if (valA !== valB) {
              wasUpdated = true;
              dd[key] = valB;
            }
          } catch (err) {
            console.log(err);
          }
        }
      });
    });

    return wasUpdated ? this.refreshCollection() : this;
  };

  public onSelect = (d: any = null): Observable<T | T[] | null | any> => {
    return this.selectionHash.asObservable().pipe(
      map(() => this.selection),
      switchMap(selection => {
        return isValue(d)
          ? of(this.filter(item => this.isSelected(item), d)).pipe(
              filter(items => !!items.length),
              map(items => (this.config.allowMultiselect ? items : items[0]))
            )
          : of(selection);
      })
    );
  };

  public onUpdated = (): Observable<any> =>
    combineLatest([this.collectionHash.asObservable(), this.selectionHash.asObservable()]);

  public onUpdating = (): Observable<boolean> => this.isUpdating$.asObservable();

  public toggleUpdating = (newState: boolean | null = null): this => {
    const isUpdating = this.isUpdating$.value;

    newState = isNull(newState) ? !isUpdating : !!newState;
    newState !== isUpdating && this.isUpdating$.next(newState);

    return this;
  };

  public getSelected = (): T | T[] | null | any => {
    const { allowMultiselect } = this.config;
    const selection = Array.from(this.selectionCore.values())
      .filter(id => this.hasItem(id))
      .map(id => this.getItem(id));

    return allowMultiselect ? selection : selection[0] || null;
  };

  public clone = (data: any | null = ASTERISK): T[] => {
    return this.getItems(data).map(d => JSON.parse(JSON.stringify({ ...d })));
  };

  /** ClassList */
  public addClass = (classNames: any, data: any, emitEvent: boolean = true): this => {
    const { classList$, getIdsList } = this;

    asArray(classNames).forEach(className => classList$.update(className, getIdsList(data), undefined, emitEvent));

    return this;
  };

  public removeClass = (classNames: any, data: any, emitEvent: boolean = true): this => {
    const { classList$, getIdsList } = this;

    asArray(classNames).forEach(className => classList$.update(className, [], getIdsList(data), emitEvent));

    return this;
  };

  public hasClass = (className: string | any, data: any, some: boolean = true): boolean => {
    const { classList$, getIdsList } = this;
    return classList$.groupHas(className, getIdsList(data), some);
  };

  public toggleClass = (className: string | any, data: any, state: boolean = null, emitEvent: boolean = true): this => {
    const { removeClass, addClass, hasClass } = this;

    if (isNull(state)) {
      asArray(data).forEach(d =>
        hasClass(className, d, false) ? removeClass(className, d, emitEvent) : addClass(className, d, emitEvent)
      );
      return this;
    } else {
      return !!state ? addClass(className, data, emitEvent) : removeClass(className, data, emitEvent);
    }
  };

  public getClassedItems = (className: string | any): T[] => {
    return this.getItems(this.classList$.read(className));
  };

  public getClassedIds = (className: string | any): any => {
    return this.classList$.read(className);
  };

  public clearClass = (className: string | any, emitEvent: boolean = true): this => {
    this.classList$.delete(className, emitEvent);
    return this;
  };

  public onClassChange = (className: string | any): Observable<any[]> => {
    const { classList$ } = this;

    return classList$.updated$.pipe(
      debounceTime(10),
      map(() => classList$.read(className)),
      map(ids => asArray(ids).join(';')),
      // distinctUntilChanged(),
      map(hashedIds => hashedIds.split(';').filter(isTrue))
    );
  };

  /** Child Collection */
  public createChildCollection = <TT = T>(d: any, config: ICollectionConfig = {}): Collection<TT> => {
    const item = this.getItem(d);
    return item?.[CollectionMethods.CREATE_CHILD_COLLECTION]<TT>(config) || new Collection<TT>(config);
  };

  public getChildCollection = <TT = T>(d: any): Collection<TT> => {
    return this.getItem(d)?.[CollectionProps.CHILD_COLLECTION];
  };

  /** Restrictions */
  public hasRestrictions = (): boolean => !!this.restrictions.size;

  public addRestrictions = (data: any): this => {
    asArray(data).forEach(d => {
      const id = this.getCollectionItemId(d);
      if (!isNull(id)) {
        this.restrictions.add(this.getCollectionItemId(d));
      }
    });
    return this;
  };

  public clearRestrictions = (data: any = ASTERISK): this => {
    if (data === ASTERISK) {
      this.restrictions.clear();
    } else {
      asArray(data).forEach(d => {
        const id = this.getCollectionItemId(d);
        if (!isNull(id)) {
          this.restrictions.delete(this.getCollectionItemId(d));
        }
      });
    }
    return this;
  };

  /** Array */
  public forEach = (fn: any = noop, slice: any = ASTERISK): this => {
    this.getItems(slice).forEach((item: T, index: number) => asFunction(fn, item, index));
    return this;
  };

  public map = <O = any>(fn: any = noop, slice: any = ASTERISK): O[] => {
    return this.getItems(slice).map((item: T, index: number) => asFunction(fn, item, index));
  };

  public some = (fn: any = noop, slice: any = ASTERISK): boolean =>
    this.getItems(slice).some((d, i) => !!asFunction(fn, d as T, i));

  public every = (fn: any = noop, slice: any = ASTERISK): boolean =>
    this.getItems(slice).every((d, i) => !!asFunction(fn, d, i));

  public filter = (fn: any = noop, slice: any = ASTERISK): T[] => {
    return this.getItems(slice).filter((d, i) => asFunction(fn, d, i));
  };

  public find = (fn: any = noop, slice: any = ASTERISK): T => {
    return this.getItems(slice).find((d, i) => asFunction(fn, d, i));
  };

  public sort = (fn: any = noop, slice: any = ASTERISK): T[] => {
    return this.getItems(slice).sort((a, b) => asFunction(fn, a, b));
  };

  /** Getters */
  public get name(): string | null {
    return this.config.name;
  }

  public get indexes(): any[] {
    return Array.from(this.collectionCore.keys());
  }

  public get items(): T[] {
    return Array.from(this.collectionCore.values());
  }

  public get size(): number {
    return this.collectionCore.size;
  }

  public get hasSelection(): boolean {
    return !!this.selectionCore.size;
  }

  public get allItemsAreSelected(): boolean {
    return this.selectionCore.size === this.collectionCore.size;
  }

  public get selection(): T | T[] | null | any {
    return this.getSelected();
  }

  public get selectable(): T[] {
    return this.getItems(this.indexes.filter(id => !this.selectionCore.has(id)));
  }

  public get parentItem(): any {
    return this.config.parentItem;
  }

  public get isUpdating(): boolean {
    return this.isUpdating$.value;
  }

  constructor(customConfig: ICollectionConfig = {}) {
    this.uid = `collection#${++Collection.collectionsCounter}`;
    Object.assign(this.config, customConfig);
  }
}
